// Pohyblivá období v uložených pohledech. Pohled uložený nad „tento týden“ má i za měsíc
// ukazovat aktuální týden. Při uložení se data v parametrech převedou na relativní značky,
// při otevření pohledu zpět na data podle dneška (Europe/Prague).

export type RelToken =
  | `day:${number}`
  | `week:${number}:start`
  | `week:${number}:end`
  | `month:${number}:start`
  | `month:${number}:end`;

export interface RelValue {
  $rel: RelToken;
}

const DAY = 86_400_000;
const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function pragueToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

const d = (iso: string) => new Date(`${iso}T00:00:00Z`);
const iso = (x: Date) => x.toISOString().slice(0, 10);
const days = (a: string, b: string) => Math.round((d(b).getTime() - d(a).getTime()) / DAY);

function monday(isoDay: string): string {
  const x = d(isoDay);
  return iso(new Date(x.getTime() - ((x.getUTCDay() + 6) % 7) * DAY));
}
function monthStart(isoDay: string, offset = 0): string {
  const x = d(isoDay);
  return iso(new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + offset, 1)));
}
function monthEnd(isoDay: string, offset = 0): string {
  const x = d(isoDay);
  return iso(new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + offset + 1, 0)));
}
function monthOffset(from: string, to: string): number {
  const a = d(from);
  const b = d(to);
  return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
}

export function resolveToken(t: RelToken, today: string): string {
  const [kind, nStr, edge] = t.split(':');
  const n = Number(nStr);
  if (kind === 'day') return iso(new Date(d(today).getTime() + n * DAY));
  if (kind === 'week') {
    const start = new Date(d(monday(today)).getTime() + n * 7 * DAY);
    return iso(edge === 'end' ? new Date(start.getTime() + 6 * DAY) : start);
  }
  return edge === 'end' ? monthEnd(today, n) : monthStart(today, n);
}

/** Převede data v parametrech na pohyblivá období vzhledem k `today`. Ostatní parametry nechá. */
export function toRelativeParams(params: Record<string, unknown>, today = pragueToday()): Record<string, unknown> {
  const out: Record<string, unknown> = { ...params };
  const dateKeys = Object.keys(params).filter((k) => typeof params[k] === 'string' && ISO.test(params[k] as string));
  const done = new Set<string>();
  // Dvojice začátek–konec: celý týden, celý měsíc, nebo měsíc do dneška.
  if (dateKeys.length === 2) {
    const [k1, k2] = dateKeys;
    const [startKey, endKey] = (params[k1] as string) <= (params[k2] as string) ? [k1, k2] : [k2, k1];
    const s = params[startKey] as string;
    const e = params[endKey] as string;
    const weekN = days(monday(today), s) / 7;
    if (s === monday(s) && days(s, e) === 6 && Number.isInteger(weekN)) {
      out[startKey] = { $rel: `week:${weekN}:start` };
      out[endKey] = { $rel: `week:${weekN}:end` };
      done.add(startKey).add(endKey);
    } else if (s === monthStart(s) && e === monthEnd(s)) {
      const n = monthOffset(today, s);
      out[startKey] = { $rel: `month:${n}:start` };
      out[endKey] = { $rel: `month:${n}:end` };
      done.add(startKey).add(endKey);
    } else if (s === monthStart(today) && e === today) {
      out[startKey] = { $rel: 'month:0:start' };
      out[endKey] = { $rel: 'day:0' };
      done.add(startKey).add(endKey);
    }
  }
  for (const k of dateKeys) {
    if (done.has(k)) continue;
    out[k] = { $rel: `day:${days(today, params[k] as string)}` };
  }
  return out;
}

const isRel = (v: unknown): v is RelValue => !!v && typeof v === 'object' && typeof (v as RelValue).$rel === 'string';

export function resolveParams(params: Record<string, unknown>, today = pragueToday()): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) out[k] = isRel(v) ? resolveToken(v.$rel, today) : v;
  return out;
}

/** Lidský popis období pohledu („tento týden“, „minulý měsíc“…), nebo null. */
export function describePeriod(params: Record<string, unknown>): string | null {
  const rels = Object.values(params).filter(isRel).map((v) => v.$rel);
  if (!rels.length) return null;
  const has = (t: string) => rels.includes(t as RelToken);
  const w = rels.find((r) => r.startsWith('week:'));
  if (w) {
    const n = Number(w.split(':')[1]);
    return n === 0 ? 'tento týden' : n === -1 ? 'minulý týden' : n < 0 ? `před ${-n} týdny` : `za ${n} týdny`;
  }
  if (has('month:0:start') && has('day:0')) return 'tento měsíc do dneška';
  const m = rels.find((r) => r.startsWith('month:'));
  if (m) {
    const n = Number(m.split(':')[1]);
    return n === 0 ? 'tento měsíc' : n === -1 ? 'minulý měsíc' : n < 0 ? `před ${-n} měsíci` : `za ${n} měsíce`;
  }
  if (rels.length === 1) {
    const n = Number(rels[0].split(':')[1]);
    return n === 0 ? 'dnes' : n === -1 ? 'včera' : n < 0 ? `před ${-n} dny` : `za ${n} dny`;
  }
  return 'pohyblivé období';
}
